import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { api } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { useFeatures } from '../lib/features';
import { formatDate, statusColors } from '../lib/utils';
import {
  BOOKING_FILTERS, bookingWhere, filterBookings, invoiceEditions, invoiceNumbers, money, orderPackages,
  paidHow, paymentsFrom, productLabel, statusLabel, contractState, todayKey,
} from '../lib/clientPage';
import { TABLES, visibleColumns } from '../lib/tableColumns';
import { mxnTotal } from '../lib/payLinks';
import { useTablePrefs } from '../hooks/useTablePrefs';
import ColumnPicker from '../components/ColumnPicker';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft, Building2, Phone, Mail, MapPin, Globe, User, UserCheck, Plus, Edit, Trash2, Hand,
  Loader2, Hash, MessageSquare, ArrowRightLeft, Hourglass, Star, Download, ChevronDown, ChevronRight,
  FileText, Receipt, ShoppingCart, Activity, Wallet, Users,
} from 'lucide-react';
import { StatusPill, EmptyMsg, showDay, apiError } from '../components/sales/salesShared';
import { ClientHistoryList, clientEvents } from '../components/sales/ClientHistoryList';
import ActivityModal from '../components/sales/ActivityModal';
import { InvoiceDetailsRow } from '../components/InvoiceDetailsRow';
import { AdvertiserModal } from './AdvertisersPage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const invoiceStatusColors = {
  draft: 'bg-slate-900/50 text-slate-400 border border-slate-700',
  sent: 'bg-blue-900/50 text-blue-400 border border-blue-800',
  unpaid: 'bg-amber-900/40 text-amber-300 border border-amber-800',
  paid: 'bg-emerald-900/50 text-emerald-400 border border-emerald-800',
  overdue: 'bg-red-900/50 text-red-400 border border-red-800',
  cancelled: 'bg-red-900/50 text-red-400 border border-red-800',
};

const th = 'py-2.5 px-3 text-left text-xs font-medium tracking-wide uppercase text-[#94A3B8]';
const td = 'py-2.5 px-3 text-sm';

const Card = ({ title, icon: Icon, action, children, testid }) => (
  <div className="bg-[#121212] border border-[#1E293B] rounded-lg" data-testid={testid}>
    <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-[#1E293B]">
      <h2 className="text-sm font-semibold text-[#F8FAFC] flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-[#3B82F6]" />} {title}
      </h2>
      {action}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const Line = ({ icon: Icon, children }) => (
  <div className="flex items-start gap-2 text-sm text-[#94A3B8]">
    <Icon className="w-4 h-4 mt-0.5 shrink-0" />
    <span className="min-w-0 break-words">{children}</span>
  </div>
);

const Stat = ({ label, value, sub, tone, testid }) => (
  <div className="bg-[#121212] border border-[#1E293B] rounded-lg p-4 min-w-0" data-testid={testid}>
    <div className="text-xs text-[#94A3B8] uppercase tracking-wide">{label}</div>
    <div className={`text-xl font-bold mt-1 truncate ${tone || 'text-[#F8FAFC]'}`}>{value}</div>
    {sub && <div className="text-xs text-[#475569] mt-0.5 truncate">{sub}</div>}
  </div>
);

const Pill = ({ className, children }) => (
  <span className={`inline-block px-2 py-0.5 text-xs rounded-full whitespace-nowrap ${className || ''}`}>{children}</span>
);

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

// --- Contacts ---------------------------------------------------------------

const emptyContact = { name: '', role: '', phone: '', email: '', is_primary: false };

const ContactModal = ({ open, onClose, clientId, contact, onSaved }) => {
  const [form, setForm] = useState(emptyContact);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setForm(contact ? {
        name: contact.name || '', role: contact.role || '', phone: contact.phone || '',
        email: contact.email || '', is_primary: !!contact.is_primary,
      } : emptyContact);
      setError('');
    }
  }, [open, contact]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('A name is required.'); return; }
    setSaving(true);
    setError('');
    try {
      if (contact) await api.put(`/contacts/${contact.id}`, form);
      else await api.post('/contacts', { ...form, advertiser_id: clientId });
      onSaved();
    } catch (err) {
      setError(apiError(err, 'Could not save this contact'));
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, type = 'text') => (
    <div className="space-y-1.5">
      <Label className="text-[#94A3B8]">{label}</Label>
      <Input
        type={type}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
        data-testid={`contact-${key}-input`}
      />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] max-w-lg" data-testid="contact-modal">
        <DialogHeader><DialogTitle>{contact ? 'Edit contact' : 'New contact'}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {field('name', 'Name *')}
            {field('role', 'Role / title')}
            {field('phone', 'Phone')}
            {field('email', 'Email', 'email')}
          </div>
          <label className="flex items-center gap-2 text-sm text-[#94A3B8]">
            <input
              type="checkbox"
              checked={form.is_primary}
              onChange={(e) => setForm({ ...form, is_primary: e.target.checked })}
              className="accent-[#3B82F6]"
              data-testid="contact-primary-input"
            />
            Primary contact
          </label>
          {error && <div className="text-sm text-red-400 bg-red-900/20 border border-red-800 rounded-md p-2">{error}</div>}
          <div className="flex justify-end gap-3 pt-2 border-t border-[#1E293B]">
            <Button type="button" variant="outline" onClick={onClose} className="border-[#1E293B] text-[#94A3B8]">Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="contact-save-btn">
              {saving ? 'Saving…' : (contact ? 'Update' : 'Add contact')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ContactsCard = ({ contacts, canEdit, onAdd, onEdit, onDelete, onEditClient }) => (
  <Card
    title={`Contacts (${contacts.length})`}
    icon={Users}
    testid="client-contacts"
    action={canEdit && (
      <Button size="sm" variant="ghost" onClick={onAdd} className="h-7 px-2 text-[#3B82F6]" data-testid="contact-add-btn">
        <Plus className="w-4 h-4 mr-1" /> Add
      </Button>
    )}
  >
    {contacts.length === 0 ? (
      <p className="text-sm text-[#475569]">No contacts on file.</p>
    ) : (
      <div className="divide-y divide-[#1E293B] -my-2">
        {contacts.map(ct => (
          <div key={ct.id} className="py-3 flex items-start gap-3" data-testid={`contact-${ct.id}`}>
            <div className="w-8 h-8 rounded-full bg-[#1E293B] flex items-center justify-center shrink-0">
              <User className="w-4 h-4 text-[#94A3B8]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-[#F8FAFC]">{ct.name || '(no name)'}</span>
                {ct.is_primary && (
                  <span className="flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide bg-[#3B82F6]/15 text-[#3B82F6]">
                    <Star className="w-3 h-3" /> Primary
                  </span>
                )}
              </div>
              {ct.role && !ct.from_client_record && <div className="text-xs text-[#94A3B8]">{ct.role}</div>}
              {ct.phone && <a href={`tel:${ct.phone}`} className="block text-xs text-[#3B82F6] mt-0.5">{ct.phone}</a>}
              {ct.email && <a href={`mailto:${ct.email}`} className="block text-xs text-[#3B82F6] truncate">{ct.email}</a>}
            </div>
            {canEdit && (
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  size="sm" variant="ghost" className="h-7 w-7 p-0 text-[#94A3B8]" title="Edit"
                  onClick={() => (ct.from_client_record ? onEditClient() : onEdit(ct))}
                  data-testid={`contact-edit-${ct.id}`}
                >
                  <Edit className="w-3.5 h-3.5" />
                </Button>
                {!ct.from_client_record && (
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400" title="Delete" onClick={() => onDelete(ct)} data-testid={`contact-delete-${ct.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    )}
  </Card>
);

// --- Tabs -------------------------------------------------------------------
// Each table draws the columns the user picked (gear icon in its header).
// `cells` maps a column key (lib/tableColumns.js) to what goes in the cell.

const dash = <span className="text-[#475569]">—</span>;
const alignOf = (c) => (c.align === 'right' ? 'text-right' : '');

const TableHead = ({ cols, picker }) => (
  <thead className="bg-[#0A0A0A]">
    <tr>
      {cols.map(c => <th key={c.key} className={`${th} ${alignOf(c)}`}>{c.label}</th>)}
      <th className="py-1 px-2 w-9 text-right">{picker}</th>
    </tr>
  </thead>
);

const Cells = ({ cols, cells, row }) => (
  <>
    {cols.map(c => (
      <td key={c.key} className={`${td} ${alignOf(c)}`}>{cells[c.key] ? cells[c.key](row) : dash}</td>
    ))}
    <td className="w-9" />
  </>
);

// The table's saved view + its gear icon.
const useView = (table, view) => {
  const prefs = useTablePrefs(table, view.ctx, view.cacheKey);
  const cols = visibleColumns(table, prefs.saved, view.ctx);
  const picker = (
    <ColumnPicker table={table} saved={prefs.saved} onChange={prefs.save} ctx={view.ctx} title={`${TABLES[table].label} columns`} />
  );
  return { cols, picker };
};

const BookingsTab = ({ data, invoiceNo, view }) => {
  const [filter, setFilter] = useState('all');
  const { cols, picker } = useView('client_bookings', view);
  const rows = filterBookings(data.bookings, filter);
  const shown = new Set(cols.map(c => c.key));
  const orderDate = Object.fromEntries((data.orders || []).map(o => [o.id, o.created_at]));
  const cells = {
    run_date: (b) => <span className="text-[#94A3B8] whitespace-nowrap">{b.run_date ? formatDate(b.run_date) : '—'}</span>,
    created: (b) => <span className="text-[#94A3B8] whitespace-nowrap">{b.created_at ? formatDate(b.created_at) : '—'}</span>,
    where: (b) => <span className="text-[#F8FAFC]">{bookingWhere(b)}</span>,
    category: (b) => <span className="text-[#94A3B8]">{b.category === 'website' ? 'Digital' : 'Print'}</span>,
    product: (b) => (
      <span className="text-[#F8FAFC]">
        {productLabel(b.product_type)}
        {!shown.has('size') && !shown.has('page') && (b.size || b.page_number) && (
          <span className="block text-xs text-[#475569]">
            {[b.size, b.page_number ? `p. ${b.page_number}` : null].filter(Boolean).join(' · ')}
          </span>
        )}
      </span>
    ),
    size: (b) => <span className="text-[#94A3B8] whitespace-nowrap">{b.size || (b.width && b.height ? `${b.width} × ${b.height}` : '—')}</span>,
    page: (b) => <span className="text-[#94A3B8] whitespace-nowrap">{[b.page_number ? `p. ${b.page_number}` : null, b.position, b.page_preference ? `wants ${b.page_preference}` : null].filter(Boolean).join(' · ') || '—'}</span>,
    rep: (b) => <span className="text-[#94A3B8] whitespace-nowrap">{b.sales_rep_name || '—'}</span>,
    list_rate: (b) => <span className="text-[#94A3B8] whitespace-nowrap">{b.list_rate != null ? money(b.list_rate) : '—'}</span>,
    discount: (b) => (b.list_rate != null && Number(b.list_rate) > Number(b.rate)
      ? <span className="text-[#94A3B8] whitespace-nowrap">−{money(Number(b.list_rate) - Number(b.rate))}</span> : dash),
    rate: (b) => (
      <span className="text-[#F8FAFC] whitespace-nowrap">
        {money(b.rate)}
        {!shown.has('list_rate') && b.list_rate != null && Number(b.list_rate) > Number(b.rate) && (
          <span className="block text-xs text-[#475569] line-through">{money(b.list_rate)}</span>
        )}
      </span>
    ),
    tax: (b) => <span className="text-[#94A3B8] whitespace-nowrap">{b.is_taxable ? `${Number(b.tax_rate) || 0}%` : 'No tax'}</span>,
    status: (b) => <Pill className={statusColors[b.status] || 'status-draft'}>{statusLabel(b.status)}</Pill>,
    invoice: (b) => (b.invoice_id
      ? <span className="font-mono text-xs text-[#3B82F6] whitespace-nowrap">{invoiceNo[b.invoice_id] || 'Invoiced'}</span> : dash),
    order: (b) => (b.order_id
      ? <span className="text-[#94A3B8] whitespace-nowrap">{orderDate[b.order_id] ? `Order of ${formatDate(orderDate[b.order_id])}` : 'Order'}</span> : dash),
    factura: (b) => (b.needs_factura ? <span className="text-[#F8FAFC]">Yes</span> : dash),
    creative: (b) => (b.creative_file
      ? <a href={b.creative_file} target="_blank" rel="noreferrer" className="text-[#3B82F6] text-xs break-all">{b.creative_filename || 'File'}</a>
      : dash),
    instructions: (b) => (b.special_instructions
      ? <span className="block text-xs text-[#94A3B8] max-w-[240px] truncate" title={b.special_instructions}>{b.special_instructions}</span> : dash),
    created_by: (b) => <span className="text-[#94A3B8] whitespace-nowrap">{b.created_by_name || '—'}</span>,
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {BOOKING_FILTERS.map(f => (
          <button
            key={f.value}
            type="button"
            onClick={() => setFilter(f.value)}
            className={`px-3 py-1 rounded-full text-xs border ${filter === f.value ? 'bg-[#3B82F6] border-[#3B82F6] text-white' : 'border-[#1E293B] text-[#94A3B8] hover:text-[#F8FAFC]'}`}
            data-testid={`bookings-filter-${f.value}`}
          >
            {f.label} ({filterBookings(data.bookings, f.value).length})
          </button>
        ))}
        {data.bookings_scoped_to_rep && <span className="text-xs text-[#475569]">Only your own bookings are listed.</span>}
      </div>
      <div className="bg-[#121212] border border-[#1E293B] rounded-lg overflow-x-auto">
        <table className="w-full" style={{ minWidth: `${Math.max(480, cols.length * 105)}px` }} data-testid="client-bookings-table">
          <TableHead cols={cols} picker={picker} />
          <tbody className="divide-y divide-[#1E293B]">
            {rows.map(b => (
              <tr key={b.id} className="hover:bg-[#1A1A1A]" data-testid={`client-booking-${b.id}`}>
                <Cells cols={cols} cells={cells} row={b} />
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <EmptyMsg text={data.bookings.length ? 'No bookings match this filter.' : 'No bookings yet.'} />}
      </div>
    </div>
  );
};

const OrdersTab = ({ data, invoiceNo, view }) => {
  const { cols, picker } = useView('client_orders', view);
  const cells = {
    created: (o) => <span className="text-[#94A3B8] whitespace-nowrap">{formatDate(o.created_at)}</span>,
    items: (o) => <span className="text-[#F8FAFC] whitespace-nowrap">{plural(o.item_count || (o.booking_ids || []).length, 'item')}</span>,
    packages: (o) => <span className="text-[#94A3B8]">{orderPackages(o.id, data.bookings).join(', ') || '—'}</span>,
    rep: (o) => <span className="text-[#94A3B8] whitespace-nowrap">{o.sales_rep_name || '—'}</span>,
    subtotal: (o) => <span className="text-[#94A3B8] whitespace-nowrap">{money(o.subtotal)}</span>,
    discount: (o) => (Number(o.discount_amount) > 0
      ? <span className="text-[#94A3B8] whitespace-nowrap">−{money(o.discount_amount)}{o.discount_type === 'percent' ? ` (${o.discount_value}%)` : ''}</span> : dash),
    total: (o) => <span className="text-[#F8FAFC] whitespace-nowrap">{money(o.total)}</span>,
    status: (o) => <Pill className={statusColors[o.status] || 'status-draft'}>{statusLabel(o.status)}</Pill>,
    invoice: (o) => (o.invoice_id
      ? <span className="font-mono text-xs text-[#3B82F6] whitespace-nowrap">{invoiceNo[o.invoice_id] || 'Invoiced'}</span> : dash),
    notes: (o) => (o.notes ? <span className="block text-xs text-[#94A3B8] max-w-[240px] truncate" title={o.notes}>{o.notes}</span> : dash),
    created_by: (o) => <span className="text-[#94A3B8] whitespace-nowrap">{o.created_by || '—'}</span>,
  };
  return (
    <div className="bg-[#121212] border border-[#1E293B] rounded-lg overflow-x-auto">
      <table className="w-full" style={{ minWidth: `${Math.max(480, cols.length * 100)}px` }} data-testid="client-orders-table">
        <TableHead cols={cols} picker={picker} />
        <tbody className="divide-y divide-[#1E293B]">
          {data.orders.map(o => (
            <tr key={o.id} className="hover:bg-[#1A1A1A]" data-testid={`client-order-${o.id}`}>
              <Cells cols={cols} cells={cells} row={o} />
            </tr>
          ))}
        </tbody>
      </table>
      {data.orders.length === 0 && <EmptyMsg text="No orders yet." />}
    </div>
  );
};

const InvoicesTab = ({ data, multiCurrency, view }) => {
  const [open, setOpen] = useState(() => new Set());
  const { cols, picker } = useView('client_invoices', view);
  const toggle = (id) => setOpen(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const download = async (inv) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/invoices/${inv.id}/pdf`, {
        responseType: 'blob',
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${inv.invoice_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF download failed:', err);
    }
  };
  const cells = {
    number: (inv) => {
      const expanded = open.has(inv.id);
      return (
        <button
          type="button"
          onClick={() => toggle(inv.id)}
          className="flex items-center gap-1.5 font-mono text-[#3B82F6] whitespace-nowrap"
          title={expanded ? 'Hide what this invoice bills' : 'Show what this invoice bills'}
          data-testid={`client-invoice-toggle-${inv.id}`}
        >
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          {inv.invoice_number}
        </button>
      );
    },
    created: (inv) => <span className="text-[#94A3B8] whitespace-nowrap">{formatDate(inv.created_at)}</span>,
    editions: (inv) => <span className="text-[#94A3B8]">{invoiceEditions(inv).join(', ') || '—'}</span>,
    items: (inv) => <span className="text-[#94A3B8]">{(inv.line_items || []).length || (inv.booking_ids || []).length}</span>,
    subtotal: (inv) => <span className="text-[#94A3B8] whitespace-nowrap">{money(inv.subtotal)}</span>,
    tax: (inv) => <span className="text-[#94A3B8] whitespace-nowrap">{money(inv.tax_total)}</span>,
    total: (inv) => <span className="font-medium text-[#F8FAFC] whitespace-nowrap">{money(inv.total)}</span>,
    exchange_rate: (inv) => (Number(inv.exchange_rate) > 0
      ? <span className="text-[#94A3B8] whitespace-nowrap">{Number(inv.exchange_rate).toFixed(4)}</span> : dash),
    total_mxn: (inv) => (mxnTotal(inv) != null
      ? <span className="text-[#94A3B8] whitespace-nowrap">{money(mxnTotal(inv), 'MXN')}</span> : dash),
    status: (inv) => <Pill className={invoiceStatusColors[inv.status] || invoiceStatusColors.draft}>{inv.status || 'draft'}</Pill>,
    emailed: (inv) => (inv.sent_at || inv.sent_by_email
      ? <span className="text-[#94A3B8] whitespace-nowrap">{inv.sent_at ? formatDate(inv.sent_at) : 'Yes'}</span> : dash),
    paid: (inv) => <span className="text-[#94A3B8] whitespace-nowrap">{inv.paid_at ? formatDate(inv.paid_at) : '—'}</span>,
    paid_how: (inv) => <span className="text-[#94A3B8] whitespace-nowrap">{paidHow(inv, data.receipts) || '—'}</span>,
    created_by: (inv) => <span className="text-[#94A3B8] whitespace-nowrap">{inv.created_by || '—'}</span>,
    pdf: (inv) => (
      <Button variant="ghost" size="sm" onClick={() => download(inv)} className="h-8 w-8 p-0 text-[#94A3B8] hover:text-[#F8FAFC]" title="Download PDF" data-testid={`client-invoice-pdf-${inv.id}`}>
        <Download className="w-4 h-4" />
      </Button>
    ),
  };
  return (
    <div className="bg-[#121212] border border-[#1E293B] rounded-lg overflow-x-auto">
      <table className="w-full" style={{ minWidth: `${Math.max(520, cols.length * 100)}px` }} data-testid="client-invoices-table">
        <TableHead cols={cols} picker={picker} />
        <tbody className="divide-y divide-[#1E293B]">
          {data.invoices.map(inv => (
            <React.Fragment key={inv.id}>
              <tr className="hover:bg-[#1A1A1A]" data-testid={`client-invoice-${inv.id}`}>
                <Cells cols={cols} cells={cells} row={inv} />
              </tr>
              {open.has(inv.id) && <InvoiceDetailsRow invoice={inv} colSpan={cols.length + 1} multiCurrency={multiCurrency} />}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      {data.invoices.length === 0 && <EmptyMsg text="No invoices yet." />}
    </div>
  );
};

const PaymentsTab = ({ data, view }) => {
  const { cols, picker } = useView('client_payments', view);
  const rows = paymentsFrom(data.invoices, data.receipts);
  const cells = {
    date: (p) => <span className="text-[#94A3B8] whitespace-nowrap">{p.date ? formatDate(p.date) : '—'}</span>,
    recorded: (p) => <span className="text-[#94A3B8] whitespace-nowrap">{p.item.created_at ? formatDate(p.item.created_at) : (p.item.paid_at ? formatDate(p.item.paid_at) : '—')}</span>,
    reference: (p) => <span className="font-mono text-xs text-[#F8FAFC] whitespace-nowrap">{p.reference}</span>,
    method: (p) => <span className="text-[#94A3B8]">{productLabel(p.method) || '—'}</span>,
    invoices: (p) => <span className="font-mono text-xs text-[#3B82F6]">{p.invoices.join(', ') || '—'}</span>,
    amount: (p) => <span className="font-medium text-emerald-400 whitespace-nowrap">{money(p.amount, p.currency)}</span>,
    exchange_rate: (p) => {
      const rate = p.kind === 'receipt' ? p.item.exchange_rate : p.item.paid_exchange_rate;
      return Number(rate) > 0 ? <span className="text-[#94A3B8] whitespace-nowrap">{Number(rate).toFixed(4)}</span> : dash;
    },
    balance: (p) => (p.kind === 'receipt'
      ? <span className={`whitespace-nowrap ${p.balance > 0.005 ? 'text-red-400' : 'text-[#475569]'}`}>{money(p.balance, p.currency)}</span> : dash),
    notes: (p) => (p.item.notes && p.kind === 'receipt'
      ? <span className="block text-xs text-[#94A3B8] max-w-[220px] truncate" title={p.item.notes}>{p.item.notes}</span> : dash),
    recorded_by: (p) => <span className="text-[#94A3B8] whitespace-nowrap">{p.kind === 'receipt' ? (p.item.created_by || '—') : (p.kind === 'online' ? 'Stripe' : '—')}</span>,
  };
  return (
    <div className="bg-[#121212] border border-[#1E293B] rounded-lg overflow-x-auto">
      <table className="w-full" style={{ minWidth: `${Math.max(480, cols.length * 100)}px` }} data-testid="client-payments-table">
        <TableHead cols={cols} picker={picker} />
        <tbody className="divide-y divide-[#1E293B]">
          {rows.map(p => (
            <tr key={p.key} className="hover:bg-[#1A1A1A]" data-testid={`client-payment-${p.key}`}>
              <Cells cols={cols} cells={cells} row={p} />
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length === 0 && <EmptyMsg text="No payments recorded yet." />}
    </div>
  );
};

// --- Page -------------------------------------------------------------------

const ClientDetailPage = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const { salesOnly, clientsLabel, clientLabel, multiCurrency } = useFeatures();
  const isSalesRep = user?.role === 'sales_rep';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reps, setReps] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [contactEdit, setContactEdit] = useState(null); // null = closed, {} = new, contact = edit

  const load = useCallback(async () => {
    setError('');
    try {
      setData(await api.get(`/advertisers/${clientId}/overview`));
    } catch (e) {
      setError(apiError(e, `Could not load this ${clientLabel.toLowerCase()}`));
    } finally {
      setLoading(false);
    }
  }, [clientId, clientLabel]);

  useEffect(() => { setLoading(true); setData(null); load(); }, [load]);
  useEffect(() => {
    api.get('/sales-reps').then(r => setReps(r || [])).catch(() => {});
    api.get('/industries').then(r => setIndustries(r || [])).catch(() => {});
  }, []);

  const c = data?.client;
  const sections = data?.sections || {};
  const summary = data?.summary || {};
  const events = useMemo(() => clientEvents(data), [data]);
  const invoiceNo = useMemo(() => invoiceNumbers(data?.invoices), [data]);
  const sortedReps = useMemo(() => [...reps].sort((a, b) => a.name.localeCompare(b.name)), [reps]);
  // Saved column views: per user and per property.
  const view = useMemo(() => ({
    ctx: { multiCurrency },
    cacheKey: `${user?.id || ''}:${user?.active_property_id || ''}`,
  }), [multiCurrency, user?.id, user?.active_property_id]);

  const tabs = [
    { key: 'activity', label: 'Activity', icon: Activity, count: data?.history_hidden ? null : events.length },
    sections.bookings && { key: 'bookings', label: 'Bookings', icon: FileText, count: data?.bookings.length },
    sections.orders && { key: 'orders', label: 'Orders', icon: ShoppingCart, count: data?.orders.length },
    sections.financials && { key: 'invoices', label: 'Invoices', icon: Receipt, count: data?.invoices.length },
    sections.financials && { key: 'payments', label: 'Payments', icon: Wallet, count: data ? paymentsFrom(data.invoices, data.receipts).length : 0 },
  ].filter(Boolean);
  const requested = params.get('tab');
  const contract = contractState(c?.contract_end_date, todayKey());
  const tab = tabs.some(t => t.key === requested) ? requested : (salesOnly || !sections.bookings ? 'activity' : 'bookings');
  const setTab = (key) => setParams(key ? { tab: key } : {}, { replace: true });

  const reassign = async (repId) => {
    setBusy(true);
    setError('');
    try {
      await api.post('/advertisers/bulk-assign', { sales_rep_id: repId === 'none' ? null : repId, ids: [clientId] });
      await load();
    } catch (e) {
      setError(apiError(e, `Could not reassign this ${clientLabel.toLowerCase()}`));
    } finally {
      setBusy(false);
    }
  };

  const claim = async () => {
    setBusy(true);
    setError('');
    try {
      await api.post(`/advertisers/${clientId}/claim`, {});
      await load();
    } catch (e) {
      setError(apiError(e, `Could not claim this ${clientLabel.toLowerCase()}`));
    } finally {
      setBusy(false);
    }
  };

  const deleteContact = async (ct) => {
    if (!window.confirm(`Delete the contact ${ct.name}?`)) return;
    try {
      await api.delete(`/contacts/${ct.id}`);
      await load();
    } catch (e) {
      setError(apiError(e, 'Could not delete this contact'));
    }
  };

  if (loading && !data) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-[#3B82F6]" /></div>;
  }

  if (!c) {
    return (
      <div className="space-y-4" data-testid="client-page">
        <Link to="/advertisers" className="inline-flex items-center gap-1 text-sm text-[#94A3B8] hover:text-[#F8FAFC]"><ArrowLeft className="w-4 h-4" /> {clientsLabel}</Link>
        <div className="text-sm text-red-400 bg-red-900/20 border border-red-800 rounded-md p-3" data-testid="client-page-error">{error || `${clientLabel} not found.`}</div>
      </div>
    );
  }

  const canEdit = data.can_edit;
  const unassigned = !c.assigned_sales_rep_id;
  const outstandingTone = summary.outstanding > 0.005 ? 'text-red-400' : 'text-[#F8FAFC]';

  return (
    <div className="space-y-6" data-testid="client-page">
      <button type="button" onClick={() => navigate('/advertisers')} className="inline-flex items-center gap-1 text-sm text-[#94A3B8] hover:text-[#F8FAFC]" data-testid="client-back">
        <ArrowLeft className="w-4 h-4" /> {clientsLabel}
      </button>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
        <div className="flex items-start gap-4 min-w-0">
          <div className="w-12 h-12 rounded-md bg-[#1E293B] flex items-center justify-center shrink-0">
            <Building2 className="w-6 h-6 text-[#3B82F6]" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl md:text-3xl font-bold text-[#F8FAFC] tracking-tight break-words" data-testid="client-name">{c.business_name}</h1>
              {salesOnly && <StatusPill status={c.sales_status} />}
            </div>
            <p className="text-sm text-[#94A3B8] mt-1">
              {[c.industry || 'No industry', c.location_type].filter(Boolean).join(' · ')}
              {' · '}
              {data.sales_rep
                ? <span className="text-[#3B82F6]">Sales rep: {data.sales_rep.name}</span>
                : <span className="text-amber-400">Unassigned</span>}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canEdit && !data.history_hidden && (
            <Button size="sm" onClick={() => setShowLog(true)} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="client-log-btn">
              <Plus className="w-4 h-4 mr-1" /> Log activity
            </Button>
          )}
          {isSalesRep && unassigned && (
            <Button size="sm" variant="outline" onClick={claim} disabled={busy} className="border-[#1E293B] bg-[#121212] text-[#F8FAFC]" data-testid="client-claim-btn">
              <Hand className="w-4 h-4 mr-1" /> Claim
            </Button>
          )}
          {data.can_assign && (
            <div className="flex items-center gap-2" data-testid="client-reassign">
              <ArrowRightLeft className="w-4 h-4 text-[#94A3B8]" />
              <Select value={c.assigned_sales_rep_id || 'none'} onValueChange={reassign} disabled={busy}>
                <SelectTrigger className="h-9 w-[180px] bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] text-sm" data-testid="client-reassign-select">
                  <SelectValue placeholder="Assign to…" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-[#1E293B]">
                  <SelectItem value="none" className="text-[#F8FAFC]">Unassigned</SelectItem>
                  {sortedReps.map(r => <SelectItem key={r.id} value={r.id} className="text-[#F8FAFC]">{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {busy && <Loader2 className="w-4 h-4 animate-spin text-[#3B82F6]" />}
            </div>
          )}
          {canEdit && (
            <Button size="sm" variant="outline" onClick={() => setShowEdit(true)} className="border-[#1E293B] bg-[#121212] text-[#94A3B8]" data-testid="client-edit-btn">
              <Edit className="w-4 h-4 mr-1" /> Edit
            </Button>
          )}
        </div>
      </div>

      {error && <div className="text-sm text-red-400 bg-red-900/20 border border-red-800 rounded-md p-2" data-testid="client-page-error">{error}</div>}

      {/* Money summary */}
      {sections.bookings && (
        <div className={`grid grid-cols-2 gap-3 ${sections.financials ? 'md:grid-cols-5' : 'md:grid-cols-2 max-w-xl'}`} data-testid="client-summary">
          <Stat label="Booked" value={money(summary.booked)} sub={plural(summary.booking_count || 0, 'booking')} testid="summary-booked" />
          <Stat
            label="Not invoiced"
            value={money(summary.uninvoiced)}
            sub={plural(summary.uninvoiced_count || 0, 'booking')}
            tone={summary.uninvoiced > 0.005 ? 'text-amber-300' : undefined}
            testid="summary-uninvoiced"
          />
          {sections.financials && (
            <>
              <Stat label="Invoiced" value={money(summary.invoiced)} sub={`${plural(summary.invoice_count || 0, 'invoice')}${summary.draft_count ? ` · ${summary.draft_count} draft` : ''}`} testid="summary-invoiced" />
              <Stat label="Paid" value={money(summary.paid)} sub={plural(summary.paid_count || 0, 'invoice')} tone="text-emerald-400" testid="summary-paid" />
              <Stat
                label="Outstanding"
                value={money(summary.outstanding)}
                sub={`${plural(summary.outstanding_count || 0, 'invoice')}${summary.overdue_count ? ` · ${summary.overdue_count} overdue` : ''}`}
                tone={outstandingTone}
                testid="summary-outstanding"
              />
            </>
          )}
        </div>
      )}

      {/* Details and contacts side by side; the tables get the full width below. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          <Card title={`${clientLabel} details`} icon={Building2} testid="client-details">
            <div className="space-y-2">
              {c.phone && <Line icon={Phone}><a href={`tel:${c.phone}`} className="text-[#3B82F6]">{c.phone}</a>{c.alt_phone ? ` · ${c.alt_phone}` : ''}</Line>}
              {c.email && <Line icon={Mail}><a href={`mailto:${c.email}`} className="text-[#3B82F6]">{c.email}</a></Line>}
              {(c.email_cc1 || c.email_cc2) && <Line icon={Mail}>CC: {[c.email_cc1, c.email_cc2].filter(Boolean).join(', ')}</Line>}
              {c.address && <Line icon={MapPin}>{c.address}{c.postal_code ? ` ${c.postal_code}` : ''}</Line>}
              {c.website && <Line icon={Globe}><a href={/^https?:\/\//i.test(c.website) ? c.website : `https://${c.website}`} target="_blank" rel="noreferrer" className="text-[#3B82F6]">{c.website}</a></Line>}
              <Line icon={UserCheck}>{data.sales_rep ? `Sales rep: ${data.sales_rep.name}` : <span className="text-amber-400">Unassigned (available)</span>}</Line>
              {contract && (
                <Line icon={FileText}>
                  <span data-testid="client-contract">{contract === 'active'
                    ? `Under contract until ${showDay(c.contract_end_date)}`
                    : `Contract ended ${showDay(c.contract_end_date)}`}</span>
                </Line>
              )}
              {data.days_until_release != null && (
                <Line icon={Hourglass}>
                  <span className={data.days_until_release <= 14 ? 'text-amber-400' : ''}>
                    {data.days_until_release === 0
                      ? 'Goes back to the unassigned pool at the next daily check unless an activity is logged'
                      : `Goes back to the unassigned pool in ${plural(data.days_until_release, 'day')} without a logged activity`}
                  </span>
                </Line>
              )}
              {c.docket_numbers && <Line icon={Hash}>Dockets: <span className="font-mono text-[#F8FAFC]">{c.docket_numbers}</span></Line>}
              {c.last_contact_date && <Line icon={MessageSquare}>Last contact: {showDay(c.last_contact_date)}</Line>}
              {c.next_follow_up_date && <Line icon={MessageSquare}>Next follow-up: {showDay(c.next_follow_up_date)}</Line>}
              {c.created_at && <p className="text-xs text-[#475569] pt-1">Added {formatDate(c.created_at)}{c.sales_rep_name ? ` by ${c.sales_rep_name}` : ''}</p>}
              {!c.phone && !c.email && <p className="text-xs text-amber-400">No phone or email on file.</p>}
            </div>
            {c.notes && <p className="mt-3 text-xs text-[#94A3B8] whitespace-pre-wrap bg-[#0A0A0A] border border-[#1E293B] rounded p-3" data-testid="client-notes">{c.notes}</p>}
          </Card>

          <ContactsCard
            contacts={data.contacts}
            canEdit={canEdit}
            onAdd={() => setContactEdit({})}
            onEdit={(ct) => setContactEdit(ct)}
            onDelete={deleteContact}
            onEditClient={() => setShowEdit(true)}
          />
      </div>

      {/* Tables: full width */}
      <div className="min-w-0 space-y-3">
          <div className="flex gap-1 overflow-x-auto border-b border-[#1E293B]" role="tablist" data-testid="client-tabs">
            {tabs.map(t => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-2 px-3 py-2 text-sm whitespace-nowrap border-b-2 -mb-px ${tab === t.key ? 'border-[#3B82F6] text-[#F8FAFC]' : 'border-transparent text-[#94A3B8] hover:text-[#F8FAFC]'}`}
                data-testid={`client-tab-${t.key}`}
              >
                <t.icon className="w-4 h-4" />
                {t.label}
                {t.count != null && <span className="text-xs text-[#475569]">{t.count}</span>}
              </button>
            ))}
          </div>

          {tab === 'activity' && (
            <ClientHistoryList data={data} events={events} salesOnly={salesOnly} />
          )}
          {tab === 'bookings' && <BookingsTab data={data} invoiceNo={invoiceNo} view={view} />}
          {tab === 'orders' && <OrdersTab data={data} invoiceNo={invoiceNo} view={view} />}
          {tab === 'invoices' && <InvoicesTab data={data} multiCurrency={multiCurrency} view={view} />}
          {tab === 'payments' && <PaymentsTab data={data} view={view} />}
      </div>

      <AdvertiserModal
        isOpen={showEdit}
        onClose={() => setShowEdit(false)}
        advertiser={c}
        industries={industries}
        salesReps={reps}
        salesOnly={salesOnly}
        clientLabel={clientLabel}
        isSalesRep={isSalesRep}
        canAssign={data.can_assign}
        onSave={() => { setShowEdit(false); load(); }}
        onIndustryAdded={() => api.get('/industries').then(r => setIndustries(r || [])).catch(() => {})}
      />
      <ActivityModal open={showLog} client={c} onClose={() => setShowLog(false)} onSaved={load} />
      <ContactModal
        open={contactEdit !== null}
        onClose={() => setContactEdit(null)}
        clientId={clientId}
        contact={contactEdit && contactEdit.id ? contactEdit : null}
        onSaved={() => { setContactEdit(null); load(); }}
      />
    </div>
  );
};

export default ClientDetailPage;
